// Replayer: plays a recorded step list back against a document. For each step it
// waits for the target to appear+become visible (MutationObserver-backed), then
// performs the action with React-safe value setting. Returns a per-step report;
// fails fast by default.

const { resolve } = require('./selector');
const { isVisible, waitFor, setNativeValue, dispatch, fireClick, sleep } = require('./dom');

class Replayer {
    constructor(opts) {
        opts = opts || {};
        this.win = opts.win || (typeof window !== 'undefined' ? window : null);
        this.doc = opts.doc || (this.win && this.win.document) || (typeof document !== 'undefined' ? document : null);
        this.timeout = opts.timeout != null ? opts.timeout : 5000;
        this.stepDelay = opts.stepDelay != null ? opts.stepDelay : 0;
        // honorDelays: replay the human's inter-step pauses (default off for speed)
        this.honorDelays = !!opts.honorDelays;
        this.onStep = typeof opts.onStep === 'function' ? opts.onStep : null;
    }

    async _waitTarget(step) {
        return waitFor(
            this.win,
            this.doc,
            () => {
                const el = resolve(this.doc, step.target);
                return el && isVisible(el) ? el : null;
            },
            { timeout: this.timeout }
        );
    }

    async _act(step, el) {
        const win = this.win;
        if (el && typeof el.scrollIntoView === 'function') {
            try { el.scrollIntoView({ block: 'center' }); } catch (_) { /* noop */ }
        }
        switch (step.action) {
            case 'click':
                fireClick(win, el);
                return;
            case 'fill':
                el.focus && el.focus();
                setNativeValue(el, step.value);
                dispatch(win, el, 'input');
                dispatch(win, el, 'change');
                el.blur && el.blur();
                return;
            case 'check': {
                const want = !!step.value;
                if (!!el.checked !== want) fireClick(win, el);
                if (!!el.checked !== want) { el.checked = want; }
                dispatch(win, el, 'change');
                return;
            }
            case 'select':
                setNativeValue(el, step.value);
                dispatch(win, el, 'input');
                dispatch(win, el, 'change');
                return;
            default:
                throw new Error('unknown action: ' + step.action);
        }
    }

    async play(steps, opts) {
        opts = opts || {};
        const failFast = opts.failFast !== false;
        const report = [];
        for (let i = 0; i < steps.length; i++) {
            const step = steps[i];
            const rec = { index: i, action: step.action, ok: false };
            try {
                if (step.action === 'navigate') {
                    // In a real browser the app drives navigation; here we just
                    // note it (full-page nav resume is a later phase). Treat as
                    // a soft checkpoint so it never fails the run.
                    rec.ok = true;
                    rec.skipped = true;
                } else {
                    if (this.honorDelays && step.delay) await sleep(this.win, step.delay);
                    const el = await this._waitTarget(step);
                    await this._act(step, el);
                    rec.ok = true;
                }
            } catch (err) {
                rec.ok = false;
                rec.error = err.message;
            }
            report.push(rec);
            if (this.onStep) { try { this.onStep(rec); } catch (_) { /* noop */ } }
            if (!rec.ok && failFast) {
                const e = new Error('Replay failed at step ' + i + ' (' + step.action + '): ' + rec.error);
                e.report = report;
                throw e;
            }
            if (this.stepDelay) await sleep(this.win, this.stepDelay);
        }
        return report;
    }
}

module.exports = { Replayer };
